/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import _ from 'lodash';
import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import { push } from 'redux-first-history';
import {
    removeUkgUserTimeZoneTransformation, convertNullableDateTimeToUkgUserDateTime, applyUkgUserTimeZoneTransformation, convertUtcDateToDateInUserTimezone,
    // eslint-disable-next-line import/no-unresolved
} from '@commonClient2/services/ukgDateTime';
import {
    UPDATE_DRAFT_BID_PROFILE,
    UPDATE_DRAFT_BIDDING_INFORMATION,
    UPDATE_DRAFT_ORIGINAL_BIDDING_INFORMATION,
    UPDATE_DRAFT_SCHEDULE_SELECTION,
    UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION,
    UPDATE_DRAFT_EMPLOYEES_SELECTION,
    UPDATE_DRAFT_ORIGINAL_EMPLOYEES_SELECTION,
    UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
    UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
    RESET_DRAFT_BID_PROFILE,
} from './bidProfileDraft.actions';
import * as api from '../../services/api-endpoints';
import {
    simplifyBidProfileDraftModels,
    simplifyScheduleTemplates,
    getDefaultEmployeeInfos,
    isScheduleSelectionEditable,
    simplifySequentialSchedule,
} from '../../services/BidProfileModelMapper';
import BriefBidProfileModelBuilder from '../../services/BriefBidProfileModelBuilder';
import { UPDATE_MANAGER_APP_DATA, formatWorkplan, loadEmployees } from '../appData';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import { BidProfileEditTab, ScheduleSource, BidProfileStatus } from '../../../Enums';
import i18n from '../../../services/i18n';
import FileService from '../../../services/FileService';

/**
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @param {boolean} isWorkplanIntegrationAvailable
 */
export const createBidProfileDraft = (storeData, isWorkplanIntegrationAvailable) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const {
        bidProfileDrafts,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const createDraftResponse = await api.createBiddingDraft();
        if (createDraftResponse.status === 200) {
            dispatch({ type: RESET_DRAFT_BID_PROFILE });
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    filteredDataviewEmployeeData: [],
                    dataviewEmployeeData: [],
                    dataviewFields: []
                } 
            });
            const newDraftInList = BriefBidProfileModelBuilder.fromBriefBidProfileDraftPresentationModel(createDraftResponse.data, storeData);
            const defaultEmployeesInfo = getDefaultEmployeeInfos();
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { draftId: createDraftResponse.data.draftId, id: createDraftResponse.data.draftId, employees: defaultEmployeesInfo } });
            setTimeout(() => dispatch(push('/Manager/form')), 0);
            let workplans = [];
            if (isWorkplanIntegrationAvailable) {
                const workplansResponse = await api.getWorkplans(createDraftResponse.data.draftId);
                /** @type {ManagerAppData.IWorkplanBrief[]} */
                workplans = workplansResponse.data.map((x) => formatWorkplan(x));
            }
            dispatch({
                    type: UPDATE_MANAGER_APP_DATA,
                    payload: {
                        bidProfileDrafts: [...bidProfileDrafts, newDraftInList],
                        workplans,
                    },
                });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.creatingBidDraft'), i18n.t('shiftBidding:toastr.failedToCreate'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.creatingBidDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
        dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { isCreateDraftActionProcessing: false } });
    }
};

export const emptyProfileDraft = () => /** @param {import('redux').Dispatch} dispatch */(dispatch) => {
    dispatch({ type: RESET_DRAFT_BID_PROFILE });
};

/**
 * @param {string} id
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const editBidProfileDraft = (id, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const [draftResponse, scheduleTemplatesResponse] = await Promise.all([api.getDraft(id), api.getBidProfileScheduleItemsDraft(id)]);
        const [draftWithoutTemplates] = simplifyBidProfileDraftModels([draftResponse.data], storeData);

        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { ...draftWithoutTemplates } });
        dispatch({ type: UPDATE_DRAFT_BIDDING_INFORMATION, payload: { isExpanded: true } });
        dispatch(loadEmployees(true, draftWithoutTemplates, { managerBidSetups: storeData.managerBidSetups }));

        const canEditScheduleSelection = isScheduleSelectionEditable(draftWithoutTemplates.status);
        const scheduleTemplates = simplifyScheduleTemplates(scheduleTemplatesResponse.data, canEditScheduleSelection);
        dispatch({ type: UPDATE_DRAFT_SCHEDULE_SELECTION, payload: scheduleTemplates });
        dispatch({ type: UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: scheduleTemplatesResponse.data.isCompleted } });

        dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { pageName: i18n.t('shiftBidding:manager.common.editBid') } });
        dispatch(push('/Manager/form'));
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.fetchingBidDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[]
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[]
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @returns
 */
export const deleteLatestDraftRebid = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });

        const response = await api.deleteBiddingDraft(draftId);

        if (response.status === 200) {
            // Parse latest rebid
            const updatedRebidBidProfile = BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(response.data, storeData);
            // Delete draft in bidProfileDrafts in store
            const drafts = storeData.bidProfileDrafts;
            const indexDraft = drafts.findIndex((x) => x.id === draftId);
            drafts.splice(indexDraft, 1);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts: [...drafts] } });
            // Add next latest rebid to bidProfiles
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles: [...storeData.bidProfiles, updatedRebidBidProfile] } });
            toastr.success(i18n.t('shiftBidding:toastr.deletingDraft'), i18n.t('shiftBidding:toastr.draftDeletedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.deletingDraft'), i18n.t('shiftBidding:toastr.failedToDelete'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.deletingDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {BidProfile.IBriefBaseProfile[]} drafts
 * @returns
 */
export const deleteDraft = (draftId, drafts) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.deleteBiddingDraft(draftId);

        if (response.status === 200) {
            const indexDraft = drafts.findIndex((x) => x.id === draftId);
            drafts.splice(indexDraft, 1);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts: [...drafts] } });
            toastr.success(i18n.t('shiftBidding:toastr.deletingDraft'), i18n.t('shiftBidding:toastr.draftDeletedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.deletingDraft'), i18n.t('shiftBidding:toastr.failedToDelete'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.deletingDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[]
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const duplicateDraft = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfileDrafts,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.duplicateBiddingDraft(draftId);
        if (response.status === 200) {
            const [duplicatedDraft] = simplifyBidProfileDraftModels([response.data], storeData);
            const duplicatedDraftBrief = BriefBidProfileModelBuilder.fromBidProfileDraftPresentationModel(response.data, storeData);
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: { 
                    bidProfileDrafts: [...bidProfileDrafts, duplicatedDraftBrief], 
                    pageName: i18n.t('shiftBidding:manager.biddingList.createNewBid'),
                    dataviewEmployeeData: [],
                    filteredDataviewEmployeeData: [],
                    dataviewFields: [],
                }
            });
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: duplicatedDraft });
            dispatch({
                type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
                payload: { 
                    employeesIds: [], 
                    hyperfindId: undefined, 
                    hyperfindLabel: '' 
                }
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_EMPLOYEES_SELECTION,
                payload: { 
                    employeesIds: [], 
                    hyperfindId: undefined, 
                    hyperfindLabel: '' 
                }
            });
            dispatch(push('/Manager/form'));
            toastr.success(i18n.t('shiftBidding:toastr.duplicatingDraft'), i18n.t('shiftBidding:toastr.draftDuplicatedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.duplicatingDraft'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.duplicatingDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  bidProfileInformation: BiddingDraft.IDraftBidProfileInformationAccordion,
 * }} storeData
 * @returns
 */
export const saveBidInformationDraft = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const { bidProfileDrafts, bidProfileInformation } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.updateBiddingInformationDraft(draftId, _.cloneDeep(bidProfileInformation));
        if (response.status === 200) {
            const latestStatus = response.data.status;
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { 
                    status: latestStatus,
                    isManagementAllowed: response.data.isManagementAllowed,
                    isViewAllowed: response.data.isViewAllowed,
                    isCreationAllowed: response.data.isCreationAllowed,
                },
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_BIDDING_INFORMATION,
                payload: {
                    ..._.cloneDeep(bidProfileInformation),
                    scheduleSource: response.data.scheduleSource,
                    bidProcessType: response.data.bidProcessType,
                    isCompleted: true,
                },
            });
            dispatch({
                type: UPDATE_DRAFT_BIDDING_INFORMATION,
                payload: {
                    scheduleSource: response.data.scheduleSource,
                    bidProcessType: response.data.bidProcessType,
                    isCompleted: true,
                    isExpanded: false,
                },
            });

            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION,
                payload: { isExpanded: true },
            });

            const indexToUpdate = bidProfileDrafts.findIndex((x) => x.id === draftId);
            bidProfileDrafts[indexToUpdate] = BriefBidProfileModelBuilder.updateBidInformationInBriefProfile(bidProfileDrafts[indexToUpdate], response.data);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts } });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingBidInformation'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidInformation'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 *
 * @param {string} draftId
 * @param {any} excelFile
 * @returns
 */
export const importExcelFileDraft = (draftId, excelFile) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.uploadExcelScheduleTemplatesDraft(draftId, excelFile);

        if (response.status === 200) {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: {
                    isUploadingSchedule: response.data.isUploadingSchedule
                }
            });
            toastr.success(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.importProcessStarted'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        if (error?.response?.data?.Message) {
            const fileName = excelFile.name.split('.').slice(0, -1).join('.');
            const count = (error?.response?.data?.Message.match(/\r\n/g) || [1]).length;
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.importErrorsDetected', { count }));
            FileService.downloadContentInFile(`${fileName}Errors`, 'txt', error.response.data.Message);
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {string[]} workplanIds
 * @param {boolean} isRefresh
 */
export const retrieveWorkplansDraft = (draftId, workplanIds, isRefresh = false) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const draftResponse = await api.selectWorkplansDraft(draftId, workplanIds, isRefresh);

        if (draftResponse.status === 200) {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: {
                    isWorkPlansRetrieved: false, // TOOD - what is this?
                    anchorDay: draftResponse.data.anchorDay,
                    isUploadingSchedule: draftResponse.data.isUploadingSchedule
                }
            });
        } else {
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
            toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
        toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BiddingDraft.IBriefBidProfileDraft[],
* }} storeData
 */
export const updateRetrieveImportDraftScheduleCompleted = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    // Update this first so that we stop pinging /status
    dispatch({
        type: UPDATE_DRAFT_BID_PROFILE,
        payload: { isUploadingSchedule: false },
    });

    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const scheduleTemplatesResponse = await api.getBidProfileScheduleItemsDraft(draftId);

        if (scheduleTemplatesResponse.status === 200) {
            const draftResponse = await api.getDraft(draftId);
            const scheduleTemplates = simplifyScheduleTemplates(scheduleTemplatesResponse.data, true);
            scheduleTemplates.isExpanded = true;
            dispatch({ type: UPDATE_DRAFT_SCHEDULE_SELECTION, payload: scheduleTemplates });
            dispatch({ type: UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: {
                    isWorkPlansRetrieved: false, // TOOD - what is this?
                    anchorDay: draftResponse.data.anchorDay,
                    isUploadingSchedule: draftResponse.data.isUploadingSchedule
                }
            });

            const { bidProfileDrafts } = storeData;
            const draft = bidProfileDrafts.find((x) => x.draftId === draftId);
            if (draft?.scheduleSource === ScheduleSource.WorkplanManager && scheduleTemplatesResponse.data.isActiveWorkplanBid && draftResponse.data?.schedulingAndVisibility?.requestPeriodEndDate) {
                // @ts-ignore
                const workplanStartDate = convertUtcDateToDateInUserTimezone(scheduleTemplatesResponse.data.activeWorkplanStartDate * 1000);
                let { requestPeriodStartDate } = draftResponse.data.schedulingAndVisibility;
                if (!requestPeriodStartDate || workplanStartDate >= requestPeriodStartDate) {
                    requestPeriodStartDate = workplanStartDate;
                }
                const requestPeriodEndDate = applyUkgUserTimeZoneTransformation(draftResponse.data.schedulingAndVisibility.requestPeriodEndDate);

                dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload: { requestPeriodStartDate, requestPeriodEndDate },
                });
                dispatch({
                    type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                    payload: { requestPeriodStartDate, requestPeriodEndDate },
                });

                draft.isForeverRequestPeriod = scheduleTemplatesResponse.data.activeWorkplanEndDate === 32503680000;
            }
        } else {
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
            toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
        toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 *
 * @param {string} draftId
 * @param {ScheduleSource} scheduleSource
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  scheduleTemplates: BiddingDraft.IDraftScheduleTemplatesAccordion,
 * }} storeData
 */
export const saveScheduleTemplateSelectionsDraft = (draftId, scheduleSource, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { bidProfileDrafts, scheduleTemplates } = storeData;
    /**
     * @type {number[] | undefined}
     */
    let selectedIds = [];
    /**
     * @param {BidProfile.IScheduleTemplateItem[] | undefined} items
     * @returns {number[]}
     */
    const getSelectedScheduleTemplateIds = (items) => (items ? items?.filter((x) => x.isSelected).map((x) => x.id) : []);
    if (scheduleSource === ScheduleSource.ExcelFile) {
        selectedIds = getSelectedScheduleTemplateIds(scheduleTemplates?.items);
    } else {
        selectedIds = scheduleTemplates?.retrievedWorkplans?.workplans.flatMap((x) => getSelectedScheduleTemplateIds(x.scheduleTemplates?.items));
    }

    if (!selectedIds || !selectedIds.length) {
        toastr.error(i18n.t('shiftBidding:toastr.savingScheduleTemplates'), i18n.t('shiftBidding:toastr.pleaseSelectAtLeastOneScheduleTemplate'));
        return;
    }
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.selectSchedulePatternTemplatesDraft(draftId, selectedIds);

        if (response.status === 200) {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { status: BidProfileStatus.Draft, managerWarnings: response.data.managerWarnings },
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION,
                payload: { ..._.cloneDeep(scheduleTemplates), isCompleted: true },
            });
            dispatch({
                type: UPDATE_DRAFT_SCHEDULE_SELECTION,
                payload: { isExpanded: false, isCompleted: true },
            });
            dispatch({
                type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
                payload: { isExpanded: true },
            });
            const indexToUpdate = bidProfileDrafts.findIndex((x) => x.id === draftId);
            bidProfileDrafts[indexToUpdate] = BriefBidProfileModelBuilder.updateScheduleTemplatesInBriefProfile(
                bidProfileDrafts[indexToUpdate],
                response.data,
                true,
            );
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts } });
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {string} bidName
 * @returns
 */
export const downloadExcelTemplateFileDraft = (draftId, bidName) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.downloadExcelTemplateDraft(draftId);

        if (response.status === 200) {
            FileService.downloadContentInFile(`${bidName}_SchedulePatternTemplate`, 'xlsx', response.data);
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.downloadingExcelFile'), i18n.t('shiftBidding:toastr.failedToDownload'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.downloadingExcelFile'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 *
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  employees: BiddingDraft.IDraftEmployeeSelectionAccordion,
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @returns
 */
export const saveEmployeeSelectionStepDraft = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfileDrafts, employees, hyperfinds,
    } = storeData;
    try {
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, [pendingTask]: begin, payload: { draftId } });
        const response = await api.updateEmployeeSelectionDraft(draftId, {
            ...employees,
            periodStartDate: removeUkgUserTimeZoneTransformation(employees.periodSelected.startDate).toISOString() || '',
            periodEndDate: removeUkgUserTimeZoneTransformation(employees.periodSelected.endDate).toISOString() || '',
        });
        if (response.status === 200) {
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { status: BidProfileStatus.Draft, managerWarnings: response.data.managerWarnings },
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_EMPLOYEES_SELECTION,
                payload: {
                    ..._.cloneDeep(employees),
                    rankedEmployees: response.data.employeeInfos.rankedEmployees,
                    isCompleted: true,
                },
            });

            const { sequentialSchedule, submissionPeriodEndDate } = response.data.schedulingAndVisibility;
            if (sequentialSchedule && Object.keys(sequentialSchedule).length !== 0) {
                const payload = {
                    sequentialSchedule: simplifySequentialSchedule(sequentialSchedule),
                    submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(submissionPeriodEndDate),
                };
                dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload,
                });
                dispatch({
                    type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                    payload,
                });
            }

            dispatch({ type: UPDATE_DRAFT_EMPLOYEES_SELECTION, payload: { isExpanded: false, isCompleted: true } });
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE, payload: { isExpanded: true } });
            const indexToUpdate = bidProfileDrafts.findIndex((x) => x.id === draftId);
            bidProfileDrafts[indexToUpdate] = BriefBidProfileModelBuilder.updateEmployeesInBriefProfile(
                bidProfileDrafts[indexToUpdate],
                response.data,
                true,
                { hyperfinds },
            );
            return dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts } });
        }
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), i18n.t('shiftBidding:toastr.failedToSave'));
        return Promise.reject;
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), error.response.data.Message);
        return Promise.reject;
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  bidProfileDraft: BiddingDraft.IBidProfileDraft,
 * }} storeData
 * @param {boolean} redirectsToSummary
 */
export const applyScheduleDraft = (draftId, storeData, redirectsToSummary = true) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { bidProfileDrafts, bidProfileDraft } = storeData;
    const { schedule, originalRequestPeriodEndDateIsForever, originalRequestPeriodEndDate } = bidProfileDraft;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        let requestPeriodEndDate = removeUkgUserTimeZoneTransformation(schedule.requestPeriodEndDate).toISOString();
        const requestPeriodEndDateEndOfDay = moment(schedule.requestPeriodEndDate).endOf('day');
        const originalRequestPeriodEndDateMoment = moment(originalRequestPeriodEndDate).endOf('day');
        if (originalRequestPeriodEndDateIsForever && originalRequestPeriodEndDateMoment.isSame(requestPeriodEndDateEndOfDay)) {
            requestPeriodEndDate = removeUkgUserTimeZoneTransformation(originalRequestPeriodEndDate).toISOString();
        }

        const response = await api.updateschedulePeriodDraft(draftId, {
            // @ts-ignore - If able to save then schedule values should not be null!
            requestPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.requestPeriodStartDate).toISOString(),
            // @ts-ignore - If able to save then schedule values should not be null!
            requestPeriodEndDate,
            // @ts-ignore - If able to save then schedule values should not be null!
            submissionPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodStartDate).toISOString(),
            // @ts-ignore - If able to save then schedule values should not be null!
            submissionPeriodEndDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodEndDate).toISOString(),
            dayOfTheWeek: Object.keys(schedule.dayOfTheWeek).filter((x) => schedule.dayOfTheWeek[x]).map((x) => parseInt(x, 10)),
            startBiddingTime: removeUkgUserTimeZoneTransformation(schedule.startBiddingTime).toISOString(),
            endBiddingTime: removeUkgUserTimeZoneTransformation(schedule.endBiddingTime).toISOString(),
            timePerEmployeeUnit: schedule.timePerEmployeeUnit,
            timePerEmployeeValue: schedule.timePerEmployeeValue,
            isAnytime: schedule.isAnytime,
            isVisibilityEnabled: false, // TODO R3
            daysBeforeVisibility: 0, // TODO R3
        });

        if (response.status === 200) {
            const { status: latestStatus, isForeverRequestPeriod } = response.data;
            dispatch({
                type: UPDATE_DRAFT_BID_PROFILE,
                payload: { status: latestStatus, isForeverRequestPeriod },
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_BID_PROFILE_SCHEDULE,
                payload: {
                    ..._.cloneDeep(schedule),
                    isCompleted: redirectsToSummary,
                },
            });

            const indexToUpdate = bidProfileDrafts.findIndex((x) => x.id === draftId);
            bidProfileDrafts[indexToUpdate] = BriefBidProfileModelBuilder.updateScheduleInBriefProfile(bidProfileDrafts[indexToUpdate], response.data);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts } });
            if (redirectsToSummary) {
                dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload: {
                        isCompleted: true,
                        isExpanded: false,
                    },
                });
                dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE,
                    payload: {
                        tabSelected: BidProfileEditTab.Summary,
                    },
                });
            }
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} draftId
 * @param {{
 *  schedule: BiddingDraft.IDraftBidProfileSchedulingAccordion,
 *  employees: BidProfile.IEmployeeSelection,
 * }} storeData
 */
export const getSequentialEmployeeScheduleDraft = (draftId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { schedule, employees } = storeData;
    if (!employees.employeesIds.length) {
        return Promise.reject;
    }
    try {
        const response = await api.sequentialEmployeeScheduleDraft(draftId, {
            // @ts-ignore - If able to save then schedule values should not be null!
            submissionPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodStartDate).toISOString(),
            dayOfTheWeek: Object.keys(schedule.dayOfTheWeek).filter((x) => schedule.dayOfTheWeek[x]).map((x) => parseInt(x, 10)),
            startBiddingTime: removeUkgUserTimeZoneTransformation(schedule.startBiddingTime).toISOString(),
            endBiddingTime: removeUkgUserTimeZoneTransformation(schedule.endBiddingTime).toISOString(),
            timePerEmployeeUnit: schedule.timePerEmployeeUnit,
            timePerEmployeeValue: schedule.timePerEmployeeValue,
            isAnytime: schedule.isAnytime,
        });
        if (response.status === 200) {
            const parsedSequentialSchedule = response.data?.employeesSchedule;
            if (parsedSequentialSchedule) {
                const payload = {
                    sequentialSchedule: simplifySequentialSchedule(parsedSequentialSchedule),
                    submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(response.data?.submissionPeriodEndDate),
                };
                return dispatch({
                    type: UPDATE_DRAFT_BID_PROFILE_SCHEDULE,
                    payload,
                });
            }
            return Promise.resolve;
        }
        toastr.error(i18n.t('shiftBidding:toastr.calculatingEstimatedSubmissionEndDate'), i18n.t('shiftBidding:toastr.failedToEstimate'));
        return Promise.reject;
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.calculatingEstimatedSubmissionEndDate'), error.response.data.Message);
        return Promise.reject;
    }
};

/**
 *
 * @param {string} draftId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[],
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 */
export const completeDraft = (draftId, storeData) =>/** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const {
        bidProfileDrafts,
        bidProfiles,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.complete(draftId);
        if (response.status === 200) {
            const newBidProfile = BriefBidProfileModelBuilder.fromBriefBidProfilePresentationModel(response.data, storeData);

            const indexDraft = bidProfileDrafts.findIndex((x) => x.id === draftId);
            const draft = bidProfileDrafts[indexDraft];
            bidProfileDrafts.splice(indexDraft, 1);

            newBidProfile.isForeverRequestPeriod = draft.isForeverRequestPeriod;
            const updatedBidProfiles = [...bidProfiles, newBidProfile];

            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: {
                    bidProfileDrafts: [...bidProfileDrafts],
                    bidProfiles: updatedBidProfiles,
                },
            });
            dispatch({ type: RESET_DRAFT_BID_PROFILE });
            dispatch(push('/Manager'));
            toastr.success(i18n.t('shiftBidding:toastr.completingBidDraft'), i18n.t('shiftBidding:toastr.bidCreatedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.completingBidDraft'), i18n.t('shiftBidding:toastr.failedToCreate'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.completingBidDraft'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isCompleteDraftActionProcessing: false } });
    }
};
